import type { ReactNode } from "react";

import { cn } from "../cn.ts";
import { GAP, responsive } from "./scale.ts";
import type { Responsive, Space } from "./scale.ts";

// Stack lays children out top to bottom; Inline left to right. Both take
// spacing from the scale and alignment by name, never a class string.

type Align = "start" | "center" | "end" | "stretch" | "baseline";
type Justify = "start" | "center" | "end" | "between";

const ALIGN: Readonly<Record<Align, string>> = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
  baseline: "items-baseline",
};

const JUSTIFY: Readonly<Record<Justify, string>> = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
};

type StackElement = "div" | "section" | "ul" | "ol" | "li" | "form" | "header" | "footer" | "nav" | "article";

export type StackProps = {
  readonly as?: StackElement;
  readonly gap?: Responsive<Space>;
  readonly align?: Align;
  readonly justify?: Justify;
  readonly children?: ReactNode;
  readonly id?: string;
  readonly onSubmit?: (event: { preventDefault(): void }) => void;
  readonly noValidate?: boolean;
  readonly "aria-label"?: string;
  readonly "aria-labelledby"?: string;
};

export function Stack({ as: Element = "div", gap = 4, align, justify, ...rest }: StackProps) {
  return (
    <Element
      className={cn(
        "flex flex-col",
        responsive(gap, GAP),
        align && ALIGN[align],
        justify && JUSTIFY[justify],
      )}
      {...rest}
    />
  );
}

export type InlineProps = Omit<StackProps, "onSubmit" | "noValidate"> & {
  readonly wrap?: boolean;
  /** Collapse to a column below the md breakpoint. */
  readonly stackBelow?: "sm" | "md";
  /** Below sm, split the row into equal halves (a field and its action, side by side). */
  readonly halvesBelow?: "sm";
};

const STACK_BELOW: Readonly<Record<"sm" | "md", string>> = {
  sm: "flex-col sm:flex-row",
  md: "flex-col md:flex-row",
};

export function Inline({
  as: Element = "div",
  gap = 3,
  align = "center",
  justify,
  wrap,
  stackBelow,
  halvesBelow,
  ...rest
}: InlineProps) {
  return (
    <Element
      className={cn(
        halvesBelow ? "grid grid-cols-2 sm:flex" : "flex",
        stackBelow ? STACK_BELOW[stackBelow] : "flex-row",
        wrap && "flex-wrap",
        responsive(gap, GAP),
        ALIGN[align],
        justify && JUSTIFY[justify],
      )}
      {...rest}
    />
  );
}
